import React, { Component } from "react";
import _ from "lodash";
import { withTranslation } from "react-i18next";
import { IconButton, Menu, MenuItem, Tooltip } from "@material-ui/core";
import styled from "styled-components";

import { WorkspacePlanTier, WorkspaceUserRole } from "common/interfaces";
import { Icon } from "js/Components/Icon";
import { deleteWorkspace, deleteWorkspaceUser } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { ShowErrorDialog, ShowDialogAsync } from "js/react/components/Dialogs/BaseDialog";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { workspaces as workspacesApi } from "apis/callables";
import LeaveWorkspaceDialog from "js/Dialogs/LeaveWorkspaceDialog";

const logger = getLogger(LogGroup.TEAMS);

const StyledIconButton = styled(IconButton)`
    &&& {
        padding: 0;
        margin:0;
    }
`;

class LeaveWorkspace extends Component {
    constructor(props) {
        super(props);
        this.state = {
            accountMenuAnchorEl: null,
            userInvites: [],
            users: []
        };
    }

    // Check users to get the total number of seats and invites used in the workspace (including guests)
    getUsedSeatCount = () => {
        const { user, workspaceId } = this.props;

        if (!user.workspacesMetadata) {
            return 0;
        }

        const { counters } = user.workspacesMetadata.find(workspaceMetaData => workspaceMetaData.id === workspaceId) ?? {};

        const users = Object.keys(counters.users).reduce((acc, key) => acc + counters.users[key], 0);

        return users + counters.userInvites;
    }

    isOnlyOwner = () => {
        const { user, workspaceId } = this.props;

        const { userRole: role, counters } = user.workspacesMetadata?.find(workspaceMetaData => workspaceMetaData.id === workspaceId) ?? {};

        if (role !== WorkspaceUserRole.OWNER) {
            return false;
        }

        return counters?.users[WorkspaceUserRole.OWNER] === 1;
    }

    // Only an owner with no other users can delete a workspace.
    canDeleteWorkspace = () => {
        return this.isOnlyOwner() && this.getUsedSeatCount() === 1;
    }

    canLeaveWorkspace = () => {
        const { user } = this.props;

        const { plan } = user.workspacesMetadata?.find(workspaceMetaData => workspaceMetaData.id === this.props.workspaceId) ?? {};

        // Enterprise workspaces: users cannot leave workspace on their own
        return !(plan && plan.tier === WorkspacePlanTier.ENTERPRISE);
    }

    switchToDefaultWorkspaceIfNeeded = async workspaceId => {
        const { user } = this.props;

        // if the workspace is the current workspace, we need to switch to the default workspace
        if (workspaceId === AppController.workspaceId) {
            await AppController.switchWorkspace(user.getDefaultWorkspaceMetadata().id);
        }
    }

    performDeleteWorkspace = async () => {
        const { user, workspaceId, onWorkspaceLeft, t } = this.props;

        const workspace = await workspacesApi.getWorkspace({ workspaceId });
        const hasSubscription = workspace.stripe?.subscriptionId;

        try {
            await withProgressDialog({
                title: t("Deleting workspace..."),
                message: t("Please wait while we delete the workspace..."),
                action: async () => {
                    if (!user.id) {
                        throw new Error("User not authenticated");
                    }

                    await this.switchToDefaultWorkspaceIfNeeded(workspaceId);

                    if (hasSubscription) {
                        await workspacesApi.cancelSubscription({ workspaceId, force: true });
                    }

                    await deleteWorkspace(workspaceId);
                    await user.loadWorkspacesMetadata();

                    // Notify parent component that workspace was deleted
                    if (onWorkspaceLeft) {
                        onWorkspaceLeft();
                    }
                }
            });
        } catch (error) {
            logger.error(error, "[LeaveWorkspace] performDeleteWorkspace() Failed to delete workspace");
            ShowErrorDialog({
                title: t("Failed to delete workspace"),
                message: error.message || t("Please try again. If the issue persists, please reach out to support@beautiful.ai.")
            });
        }
    }

    performLeaveWorkspace = async () => {
        const { user, workspaceId, onWorkspaceLeft, t } = this.props;

        try {
            await withProgressDialog({
                title: t("Leaving workspace..."),
                message: t("Please wait while we transfer your presentations..."),
                action: async () => {
                    if (!user.id) {
                        throw new Error("User not authenticated");
                    }

                    await this.switchToDefaultWorkspaceIfNeeded(workspaceId);

                    await deleteWorkspaceUser({
                        workspaceId,
                        uid: user.id,
                        transferOption: "self"
                    });

                    await user.loadWorkspacesMetadata();

                    // Notify parent component that workspace was left
                    if (onWorkspaceLeft) {
                        onWorkspaceLeft();
                    }
                }
            });
        } catch (error) {
            logger.error(error, "[LeaveWorkspace] performLeaveWorkspace() Failed to leave workspace");
            ShowErrorDialog({
                title: t("Failed to leave workspace"),
                message: error.message || t("Please try again. If the issue persists, please reach out to support@beautiful.ai.")
            });
        }
    }

    handleLeaveWorkspace = async () => {
        const { t } = this.props;
        this.setState({ accountMenuAnchorEl: null });

        // Only an owner with no other users can delete a workspace.
        const onlyOwnerInWorkspace = this.canDeleteWorkspace();

        const title = onlyOwnerInWorkspace ? t("Are you sure you want to delete this workspace?") : t("Are you sure you want to leave your workspace?");
        const message = onlyOwnerInWorkspace ? t("Your workspace will be permanently deleted along with all of its content.") : t("You will no longer be able to access this workspace. Any presentations you own will transfer to a workspace owner.");
        const leaveButtonText = onlyOwnerInWorkspace ? t("Delete Workspace") : t("Leave Workspace");

        const accepted = await ShowDialogAsync(LeaveWorkspaceDialog, {
            title,
            message,
            leaveButtonText
        });

        if (!accepted) {
            return;
        }

        if (onlyOwnerInWorkspace) {
            await this.performDeleteWorkspace();
        } else {
            await this.performLeaveWorkspace();
        }
    }

    renderLeaveWorkspaceMenuItem = () => {
        const { t } = this.props;
        const canLeave = this.canLeaveWorkspace();

        // if it's enterprise user and not an owner, we won't allow users this option
        if (!canLeave) {
            return null; // Don't show menu item for Enterprise non-owners
        }

        // Disable Leave when only owner with other members (must assign another owner first).
        const isOnlyOwnerWithOtherMembers = this.isOnlyOwner() && this.getUsedSeatCount() > 1;

        const menuItem = (
            <MenuItem
                disabled={isOnlyOwnerWithOtherMembers}
                onClick={e => {
                    e.stopPropagation();
                    this.handleLeaveWorkspace();
                }}
            >
                {t("Leave Workspace")}
            </MenuItem>
        );

        if (isOnlyOwnerWithOtherMembers) {
            return (
                <Tooltip title={t("You cannot leave this workspace because you are the only owner. Assign another member as an owner before you can leave")}>
                    <span>
                        {menuItem}
                    </span>
                </Tooltip>
            );
        }

        return menuItem;
    }

    render() {
        const { color, user } = this.props;
        const { accountMenuAnchorEl } = this.state;

        // If the user cannot leave the workspace, we don't need to show the leave workspace menu
        if (!this.canLeaveWorkspace()) {
            return null;
        }

        // if the user has only one workspace, we don't need to show the leave workspace menu
        // More than likely, they want to delete their account at that point.
        if (user.workspacesMetadata?.length === 1) {
            return null;
        }

        // if the workspace is the default workspace, we don't want to show the leave workspace menu
        if (user.getDefaultWorkspaceMetadata().id === this.props.workspaceId) {
            return null;
        }

        return (
            <>
                <StyledIconButton
                    aria-label="more"
                    aria-controls="account-menu"
                    aria-haspopup="true"
                    onClick={e => {
                        e.stopPropagation();
                        this.setState({ accountMenuAnchorEl: e.currentTarget });
                    }}
                    onMouseDown={e => {
                        e.stopPropagation();
                    }}
                >
                    <Icon color={color}>more_vert</Icon>
                </StyledIconButton>
                <Menu
                    getContentAnchorEl={null}
                    id="account-menu"
                    anchorEl={accountMenuAnchorEl}
                    keepMounted
                    open={Boolean(accountMenuAnchorEl)}
                    onClose={event => {
                        if (event) {
                            event.stopPropagation();
                        }
                        this.setState({ accountMenuAnchorEl: null });
                    }}
                    disableAutoFocusItem
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                >
                    {this.renderLeaveWorkspaceMenuItem()}
                </Menu>
            </>
        );
    }
}

export default withTranslation()(AppController.withState(LeaveWorkspace));

